/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { NetworkConfig, useNetworkConfig } from "hooks/useNetworkButtonConfig";
import { cloneDeep, values } from "lodash-es";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Chain } from "viem/chains";
import { mainnet, useNetwork, useSwitchNetwork } from "wagmi";
import { ReactComponent as CheckIcon } from "../../img/header_icons/check.svg";
import { GlobalStore } from "../../store/global/GlobalStore";
import { SUPPORTED_NETWORKS } from "../../types/network";
import { getChainFromId, isStoredNetworkOutOfSync } from "../../utils/chainUtils";
import { updateUrlParams, URL_KEYS } from "../../utils/urlUtils";
import { MODAL_SELECTORS } from "../Modal/instance/modalSelectors";
import { NetworkButton } from "./NetworkButton";
import "./NetworkDropdown.css";
import { NetworkDropdownContainer } from "./elements/NetworkDropdownContainer";
import { useSelectedNetworkConfig } from "../../hooks/useSelectedNetworkConfig";
import { Text } from "../general/Text";
import { FlexBetweenContainer } from "../../containers/FlexBetweenContainer";
import { FlexContainer } from "../../containers/FlexContainer";
import { NETWORK_NAME_TO_DISPLAY } from "../../config/consts";
import LoadingEllipse from "../ButterComponents/LoadingEllipse/LoadingEllipse";

export interface NetworkDropdownProps {}

export const NetworkDropdown: React.FC<NetworkDropdownProps> = () => {
  const network = useNetwork();
  const theme = GlobalStore.useState(s => s.theme);
  const modalSelector = GlobalStore.useState(s => s.modalSelector);
  const storeNetwork = GlobalStore.useState(s => s.network);
  const address = GlobalStore.useState(s => s.address);
  const networkId = network.chain?.id ?? storeNetwork.id;
  const containerRef = useRef<HTMLDivElement>(null);
  const networkDropdownRef = useRef<HTMLDivElement>(null);
  const [isRequestingTokens, setIsRequestingTokens] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [chainSwitchingTo, setChainSwitchingTo] = useState<Chain | undefined>(undefined);
  const { switchNetwork } = useSwitchNetwork({
    onSuccess: () => {
      GlobalStore.update(s => {
        s.isChainSwitching = false;
        s.quoteError = undefined;
      });
      setIsDropdownOpen(false);
      setChainSwitchingTo(undefined);
    },
    onError: error => {
      console.error(error);
      GlobalStore.update(s => {
        s.isChainSwitching = false;
      });
      setIsDropdownOpen(false);
      setChainSwitchingTo(undefined);
      Sentry.captureException(`Could not switch networks`, { extra: { error } });
      updateUrlParams(URL_KEYS.NETWORK, storeNetwork.name.toLowerCase(), window.location.pathname);
    },
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const currentNetworkConfig: NetworkConfig = useSelectedNetworkConfig(networkId);
  const networkConfig = useNetworkConfig();
  const networksNotSelected = values(networkConfig).filter(
    (config: NetworkConfig) => config.id !== currentNetworkConfig.id
  );
  const orderedNetworks = [currentNetworkConfig, ...networksNotSelected];
  // eslint-disable-next-line
  const defaultConfig = networkConfig[mainnet.id] as NetworkConfig;
  const propsToSpread = currentNetworkConfig !== undefined ? currentNetworkConfig : defaultConfig;

  const updateChain = useCallback((network: Chain | undefined) => {
    const chainMatch = SUPPORTED_NETWORKS.find(entry => entry.id === network?.id);
    if (network && chainMatch) {
      GlobalStore.update(s => {
        s.network = cloneDeep(chainMatch) as typeof s.network;
      });
    }
    setIsRequestingTokens(false);
  }, []);

  useEffect(() => {
    // First test if the network is supported
    const isSupportedNetwork = SUPPORTED_NETWORKS.map(network => network.id).includes(
      network.chain?.id ?? -1
    );
    if (network.chain && !isSupportedNetwork) {
      GlobalStore.update(s => {
        s.modalSelector = MODAL_SELECTORS.SWITCH_NETWORK;
      });
      return;
    } else {
      if (modalSelector === MODAL_SELECTORS.SWITCH_NETWORK) {
        GlobalStore.update(s => {
          s.modalSelector = undefined;
        });
      }
    }

    if (address && isStoredNetworkOutOfSync(storeNetwork, network.chain) && !isRequestingTokens) {
      setIsRequestingTokens(true);
      void updateChain(network.chain);
    }
  }, [
    address,
    isRequestingTokens,
    modalSelector,
    network,
    storeNetwork,
    storeNetwork.id,
    updateChain,
  ]);

  useEffect(() => {
    if (chainSwitchingTo !== undefined) {
      GlobalStore.update(s => {
        s.isChainSwitching = true;
      });
    }
  }, [chainSwitchingTo]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef?.current &&
        !containerRef.current.contains(event.target as Node) &&
        chainSwitchingTo === undefined
      ) {
        if (
          networkDropdownRef.current &&
          !networkDropdownRef.current.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false);
        }
      }
    },
    [containerRef, networkDropdownRef, chainSwitchingTo]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleOnClick = useCallback(
    (networkId: number) => {
      const newChain = getChainFromId(networkId);
      // First get metadata for the new chain
      if (newChain && networkId !== network?.chain?.id) {
        if (switchNetwork) {
          // Remove network from url to prevent network switch back
          updateUrlParams(
            URL_KEYS.NETWORK,
            newChain?.name.toLowerCase() ?? "",
            window.location.pathname
          );
          setChainSwitchingTo(getChainFromId(networkId));
          switchNetwork(networkId);
        } else {
          // If switchNetwork is not defined - a wallet is not connect - just update state/
          if (newChain) {
            void updateChain(newChain);
          }
        }
      }
    },
    [network?.chain?.id, switchNetwork, updateChain]
  );

  return (
    <NetworkDropdownContainer theme={theme} data-testid={"network-dropdown"} ref={containerRef}>
      <NetworkButton
        onClick={() => setIsDropdownOpen(true)}
        {...propsToSpread}
        isDropdownOpen={isDropdownOpen}
        ref={buttonRef}
      >
        <NetworkDropdownContent
          isDisplayed={isDropdownOpen}
          buttonSize={buttonRef.current?.clientWidth}
          data-testid={"network-dropdown-dialog"}
          ref={networkDropdownRef}
        >
          {Object.values(orderedNetworks).map(props => {
            const isSelected = networkId === props.id;
            const Icon = props.icon;
            return (
              <NetworkButtonRow
                key={props.id}
                isSelected={
                  (isSelected && chainSwitchingTo === undefined) ||
                  chainSwitchingTo?.id === props.id
                }
                data-testid={`${
                  networkId === props.id ? "selected-" : `${props.id}-`
                }network-dropdown-option`}
                onClick={() => !isSelected && handleOnClick(Number(props.id))}
              >
                <FlexBetweenContainer widthExpand={true}>
                  <FlexContainer gap={8}>
                    <Icon width={22} height={22} color={props.networkIconColor} />
                    <Text textStyle="Title" size="XS">
                      {NETWORK_NAME_TO_DISPLAY[props.id]}
                    </Text>
                  </FlexContainer>
                  {isSelected && <CheckIcon stroke={theme["Colour ramps"].Green["500"]} />}
                  {chainSwitchingTo?.id === props.id && <LoadingEllipse />}
                </FlexBetweenContainer>
              </NetworkButtonRow>
            );
          })}
        </NetworkDropdownContent>
      </NetworkButton>
    </NetworkDropdownContainer>
  );
};

// Sub-components
const NetworkButtonRow = styled.div<{ isSelected: boolean }>`
  background: ${props => (props.isSelected ? props.theme.surface.main.secondary.hover : "none")};

  &:hover {
    background-color: ${props => props.theme.surface.alt.secondary.hover};
  }

  border-radius: 14px;
  display: flex;
  padding: 10px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

export const NetworkDropdownContent = styled.div<{
  buttonSize: number | undefined;
  isDisplayed: boolean;
}>`
  background-color: ${props => props.theme.surface.alt.secondary.default};
  box-shadow: ${props => props.theme.shadow.overlay};
  border-radius: 22px;
  position: absolute;
  min-width: 184px;
  padding: 8px;
  color: ${props => props.theme.font.primary.default};
  top: -2px;
  right: -2px;
  display: ${props => (props.isDisplayed ? "block" : "none")};
  z-index: 999;
`;
